import React from 'react';
import { compose } from 'redux';
import classnames from 'classnames';
import WidgetHeader from '../WidgetHeader';
import WidgetHeaderMobile from '../WidgetHeaderMobile';
import WidgetEditControls from '../WidgetEditControls';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-types-papi';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import IconTrashcan from '@wix/da-shared-react/pkg/Icons/Trashcan';
import {
  BiEventType,
  BiLink,
  BiLoggerContextProvider,
} from '@wix/da-shared-react/pkg/biLogger';
import { ModuleType } from '../../../../../types/modules';
import { ZoneType } from '../../../../../types/layout';

import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import withConfirmationModal, {
  WithConfirmationModal,
} from '../../_hoc/withConfirmationModal';
import { GallectionType } from '../../../../../types/gallection';
import s from './WidgetBox.scss';

export interface Props {
  moduleId: number;
  moduleType: ModuleType;
  moduleVersion: number;
  zone: ZoneType;
  section: string;
  children: React.ReactNode;
  className?: string;
  header: string | React.ReactNode;
  headerExtras?: React.ReactNode;
  count?: number | string;
  allowEditOnMobile?: boolean;
  autoShowWidgetConfig?: boolean;
  canUserEditWidget?: boolean;
  isMobile?: boolean;
  isNewlyAddedModule?: boolean;
  isCurrentUserProfileOwner: boolean;
  isHeaderStickyOnMobile?: boolean;
  isEmpty?: boolean;
  emptyView?: React.ReactNode;
  hasMore?: boolean;
  profileOwnerUser: PapiUser;
  seeAllUrl?: string;
  seeAllText?: string;
  widgetMenuRenderer?: (closeMenu) => React.ReactNode;
  widgetMoveUp?: () => void;
  widgetMoveDown?: () => void;
  widgetRemove?: () => void;
  confirmWidgetRemoval?: boolean;
  editControlsTooltipTitle?: string;
  editControlsTooltipContent?: React.ReactNode;
  withFTUE?: boolean;
  updateModule?: (payload: any) => void;
  sourceType?: GallectionType;
  dataHook?: string;
}

export interface State {
  isWidgetConfigShown: boolean;
}

const enhance = compose(
  withMobileContext,
  withConfirmationModal,
  withTranslation()
);
export class WidgetBox extends React.Component<
  Props & WithTranslation & WithConfirmationModal,
  State
> {
  state = { isWidgetConfigShown: false };
  render() {
    const {
      isMobile,
      className,
      header,
      count,
      children,
      isHeaderStickyOnMobile,
      isEmpty,
      emptyView,
      moduleType,
      seeAllUrl,
      dataHook,
    } = this.props;

    if (isEmpty && !emptyView) {
      return null;
    }

    const headerContents = seeAllUrl ? this.renderSeeAllLink(header) : header;

    return (
      <BiLoggerContextProvider
        value={{ widgetname: moduleType, sectionname: moduleType }}
      >
        <div
          data-role="widget"
          className={classnames(s['root'], className)}
          data-hook={dataHook}
        >
          <ErrorBoundary>
            {isMobile ? (
              <WidgetHeaderMobile
                header={headerContents}
                count={count}
                isSticky={isHeaderStickyOnMobile}
                extras={this.renderHeaderExtraElements()}
              />
            ) : (
              <WidgetHeader
                header={headerContents}
                count={count}
                extras={this.renderHeaderExtraElements()}
              />
            )}
            {isEmpty ? emptyView : children}
          </ErrorBoundary>
        </div>
      </BiLoggerContextProvider>
    );
  }

  renderSeeAllLink(contents, role?, className?) {
    const { seeAllUrl, profileOwnerUser } = this.props;
    return seeAllUrl && contents ? (
      <BiLink
        biData={{
          evid: BiEventType.SEE_ALL_CLICK,
          creatorid: profileOwnerUser.useridUuid,
          typeid: undefined,
          itemid: undefined,
        }}
        href={seeAllUrl as string}
        {...(className && { className })}
        {...(role && { 'data-role': role })}
      >
        {contents}
      </BiLink>
    ) : null;
  }

  renderHeaderExtraElements() {
    return (
      <div className={s['header-extras']}>
        {this.props.headerExtras}
        {this.renderEditControls()}
        {this.renderWidgetConfig()}
        {this.renderRightSideSeeAllLink()}
      </div>
    );
  }

  renderRightSideSeeAllLink() {
    const { t, hasMore, seeAllText, withFTUE } = this.props;

    const shouldRenderSeeAllLink = hasMore === undefined || hasMore;
    const contents = seeAllText || t!('common.see_all');
    const role = withFTUE ? 'ftue-control' : 'see-all-link';
    const linkClass = s['see-all'];
    return shouldRenderSeeAllLink
      ? this.renderSeeAllLink(contents, role, linkClass)
      : null;
  }

  renderEditControls() {
    const {
      isMobile,
      isCurrentUserProfileOwner,
      canUserEditWidget,
      allowEditOnMobile,
      widgetMoveUp,
      widgetMoveDown,
      widgetRemove,
      widgetMenuRenderer,
      editControlsTooltipTitle,
      editControlsTooltipContent,
      withFTUE,
    } = this.props;

    if (!isCurrentUserProfileOwner || (isMobile && !allowEditOnMobile)) {
      return null;
    }

    return (
      <WidgetEditControls
        className={s['edit-widget-controls']}
        onMoveUp={widgetMoveUp}
        onMoveDown={widgetMoveDown}
        onRemove={widgetRemove && this.handleWidgetRemove}
        onEdit={
          widgetMenuRenderer && !!canUserEditWidget
            ? this.showWidgetConfig
            : undefined
        }
        withFTUE={withFTUE}
        tooltipTitle={editControlsTooltipTitle}
        tooltipContent={editControlsTooltipContent}
      />
    );
  }

  renderWidgetConfig() {
    const {
      widgetMenuRenderer,
      autoShowWidgetConfig,
      isNewlyAddedModule,
    } = this.props;
    const { isWidgetConfigShown } = this.state;

    if (!widgetMenuRenderer) {
      return null;
    }

    if (isWidgetConfigShown || (isNewlyAddedModule && autoShowWidgetConfig)) {
      return widgetMenuRenderer && widgetMenuRenderer(this.hideWidgetConfig);
    }
  }

  showWidgetConfig = () => {
    this.setState({ isWidgetConfigShown: true });
  };

  hideWidgetConfig = () => {
    const { isNewlyAddedModule, updateModule } = this.props;
    this.setState({ isWidgetConfigShown: false });
    isNewlyAddedModule &&
      updateModule &&
      updateModule({
        id: this.props.moduleId,
        moduleMeta: {
          isNewlyAdded: false,
        },
      });
  };

  handleWidgetRemove = () => {
    const {
      widgetRemove,
      confirmWidgetRemoval,
      pushConfirmationModal,
      t,
    } = this.props;
    if (confirmWidgetRemoval) {
      pushConfirmationModal({
        title: t('widgets.common.removeWidget.header'),
        text: t('widgets.common.removeWidget.body'),
        confirmBtnLabel: t('widgets.common.removeWidget.confirmBtnLabel'),
        cancelBtnLabel: t('widgets.common.removeWidget.cancelBtnLabel'),
        icon: <IconTrashcan size={IconSize.MEGA} />,
        onConfirm: widgetRemove,
      });
    } else {
      widgetRemove && widgetRemove();
    }
  };
}

export default enhance(WidgetBox) as React.ComponentType<Props>;
